import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAction, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useAuth } from "../hooks/useAuth";
import {
  clearPendingPurchaseContext,
  getPendingPurchaseContext,
  getPurchaseTrackingContext,
} from "../lib/pendingPurchase";
import { t } from "../i18n";
import { WEB_ACTION_TIMEOUT_MS, withTimeout } from "../lib/withTimeout";
import { trackBrowserPurchase } from "../lib/analytics";

type Phase =
  | "waiting_webhook"
  | "premium_confirmed"
  | "creating_monitor"
  | "monitor_error"
  | "done"
  | "timeout";

type CreateMonitorResult = {
  success?: boolean;
  error?: string;
};

export function PaywallSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, isAuthenticated, isLoading: authLoading } = useAuth();
  const createMonitor = useAction(api.monitors.createMonitorAfterPurchase);

  const pendingPurchase = getPendingPurchaseContext();
  const handle = searchParams.get("handle") ?? pendingPurchase?.handle ?? "";
  const previewSessionId =
    searchParams.get("previewSessionId") ?? pendingPurchase?.previewSessionId ?? "";
  const purchaseTrackingContext = getPurchaseTrackingContext(
    typeof window !== "undefined" ? window.location.search : "",
    pendingPurchase,
  );

  const [phase, setPhase] = useState<Phase>("waiting_webhook");
  const [elapsed, setElapsed] = useState(0);
  const [monitorError, setMonitorError] = useState<string | null>(null);
  const monitorCreatedRef = useRef(false);
  const purchaseTrackedRef = useRef(false);

  const subscriptionStatus = useQuery(
    api.monitors.getUserSubscriptionStatus,
    session ? {} : "skip",
  );

  const isPremium = subscriptionStatus?.isPremium ?? false;

  useEffect(() => {
    if (
      purchaseTrackedRef.current ||
      !purchaseTrackingContext
    ) {
      return;
    }

    purchaseTrackedRef.current = true;
    trackBrowserPurchase(purchaseTrackingContext);
  }, [purchaseTrackingContext]);

  // Polling: check isPremium every 2 seconds
  useEffect(() => {
    if (phase !== "waiting_webhook") return;

    const maxWaitMs = 15_000;
    const interval = setInterval(() => {
      setElapsed((prev) => {
        const next = prev + 2000;
        if (next >= maxWaitMs) {
          setPhase("timeout");
          clearInterval(interval);
        }
        return next;
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [phase]);

  // Detect premium confirmation
  useEffect(() => {
    if (phase !== "waiting_webhook") return;
    if (isPremium) {
      setPhase("premium_confirmed");
    }
  }, [isPremium, phase]);

  // Create monitor after premium confirmed (onboarding flow)
  useEffect(() => {
    if (phase !== "premium_confirmed") return;
    if (!handle || monitorCreatedRef.current) {
      setPhase("done");
      return;
    }

    monitorCreatedRef.current = true;
    setPhase("creating_monitor");

    withTimeout(
      createMonitor({
        handle,
        source: "onboarding",
        previewSessionId: previewSessionId || undefined,
      }),
      WEB_ACTION_TIMEOUT_MS,
      "Web monitor action timed out",
    )
      .then((result) => {
        const createResult = result as CreateMonitorResult | undefined;
        if (createResult?.success) {
          setPhase("done");
          return;
        }

        monitorCreatedRef.current = false;
        setMonitorError(createResult?.error ?? t("paywallErrorStartMonitoring"));
        setPhase("monitor_error");
      })
      .catch((err) => {
        console.error("Failed to create monitor after purchase", err);
        monitorCreatedRef.current = false;
        setMonitorError(t("paywallErrorStartMonitoring"));
        setPhase("monitor_error");
      });
  }, [createMonitor, phase, handle, previewSessionId]);

  // Navigate to /app when done
  useEffect(() => {
    if (phase === "done") {
      clearPendingPurchaseContext();
      const timer = setTimeout(() => navigate("/app"), 800);
      return () => clearTimeout(timer);
    }
  }, [phase, navigate]);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="st-page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <div className="st-paywall-success">
          <div className="st-paywall-success-spinner" />
          <p>{t("commonLoading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="st-page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "var(--gray-light, #fafafa)" }}>
      <div className="st-paywall-success" style={{ textAlign: "center", padding: "40px 24px", maxWidth: "420px" }}>
        {phase === "waiting_webhook" && (
          <>
            <div className="st-paywall-success-spinner" style={{ margin: "0 auto 20px", width: "48px", height: "48px", border: "4px solid var(--pink-pastel, #fdf2f2)", borderTopColor: "var(--pink-primary, #ff6b8a)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--slate-900, #171717)", marginBottom: "8px" }}>
              {t("paywallActivatingPlan")}
            </h1>
            <p style={{ color: "var(--slate-500, #737373)", fontSize: "0.9375rem" }}>
              {t("billingContinueInAppBody")}
            </p>
            <p style={{ color: "var(--slate-400, #a3a3a3)", fontSize: "0.8125rem", marginTop: "12px" }}>
              {elapsed < 15000 ? `${Math.ceil(elapsed / 1000)}s...` : ""}
            </p>
          </>
        )}

        {(phase === "premium_confirmed" || phase === "creating_monitor") && (
          <>
            <div className="st-paywall-success-spinner" style={{ margin: "0 auto 20px", width: "48px", height: "48px", border: "4px solid var(--pink-pastel, #fdf2f2)", borderTopColor: "var(--pink-primary, #ff6b8a)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--slate-900, #171717)", marginBottom: "8px" }}>
              {t("statusActive")}
            </h1>
            <p style={{ color: "var(--slate-500, #737373)", fontSize: "0.9375rem" }}>
              {handle ? t("paywallSettingUpProfile", { handle }) : t("commonLoading")}
            </p>
          </>
        )}

        {phase === "monitor_error" && (
          <>
            <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--slate-900, #171717)", marginBottom: "8px" }}>
              {t("paywallMonitorSetupFailedTitle")}
            </h1>
            <p style={{ color: "var(--slate-500, #737373)", fontSize: "0.9375rem", marginBottom: "20px" }}>
              {monitorError ?? t("paywallMonitorSetupFailedBody")}
            </p>
            <button
              onClick={() => {
                setMonitorError(null);
                setPhase(isPremium ? "premium_confirmed" : "waiting_webhook");
              }}
              style={{
                background: "var(--pink-primary, #ff6b8a)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "1rem",
                cursor: "pointer",
              }}
            >
              {t("commonRetry")}
            </button>
          </>
        )}

        {phase === "timeout" && (
          <>
            <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--slate-900, #171717)", marginBottom: "8px" }}>
              {t("paywallProcessingPaymentTitle")}
            </h1>
            <p style={{ color: "var(--slate-500, #737373)", fontSize: "0.9375rem", marginBottom: "20px" }}>
              {t("paywallProcessingPaymentBody")}
            </p>
            <button
              onClick={() => navigate("/app")}
              style={{
                background: "var(--pink-primary, #ff6b8a)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "1rem",
                cursor: "pointer",
              }}
            >
              {t("appDashboard")}
            </button>
          </>
        )}

        {phase === "done" && (
          <>
            <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--slate-900, #171717)", marginBottom: "8px" }}>
              {t("paywallSuccessTitle")}
            </h1>
            <p style={{ color: "var(--slate-500, #737373)", fontSize: "0.9375rem" }}>
              {t("paywallSuccessBody")}
            </p>
          </>
        )}

        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}
